import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography, useTheme } from '@mui/material'
import React, { memo, useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { BatteryWidgetData } from 'src/Libs/dashboard-widgets/hooks/use-data-for-battery-widget'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import WidgetBase from './base-card/WidgetBase'
import { WidgetAnimationDirection } from './base-card/types'
import { DASHBOARD_BATTERY_WIDGET_ID } from 'src/Libs/qa/ids'
import { isFlowTheme } from 'src/Libs/utils/theme-helpers'

type Props = BatteryWidgetData

const useStyles = makeStyles()(theme => ({
  valueContainer: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  value: {
    marginBottom: '-1px',
  },
}))

const BatteryWidget: React.FC<Props> = ({ stateOfCharge, isCharging, isDischarging, subtitle, isChargeLockActive, isDischargeLockActive }) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const link = useMemo(() => `/installation/${installationId}/${storageId}/properties/battery`, [installationId, storageId])
  const { isStorageOnline } = useContext(InstallationOnlineStatusContext)
  const isIdle = !isCharging && !isDischarging && !isChargeLockActive && !isDischargeLockActive

  const getAnimationDirection = (): WidgetAnimationDirection | undefined => {
    if (isCharging) {
      return 'right'
    }
    if (isDischarging) {
      return 'left'
    }
    return undefined
  }

  return (
    <WidgetBase
      title={t('dashboard.liveView.widgets.battery.title')}
      subtitle={isIdle ? null : subtitle}
      stateOfCharge={stateOfCharge}
      animationDirection={getAnimationDirection()}
      link={isFlowTheme ? undefined : link}
      isOffline={!isStorageOnline}
      dataQa={DASHBOARD_BATTERY_WIDGET_ID}
      progressBarColor={theme.palette.batteryWidget.progressBar}
      progressBarStripesColor={theme.palette.batteryWidget.progressBarStripes}
    >
      <Box className={classes.valueContainer}>
        <Typography variant="h1" className={classes.value}>
          {stateOfCharge}
          <Typography variant="h2" component="span">{` ${t('affixes.percent')}`}</Typography>
        </Typography>
      </Box>
    </WidgetBase>
  )
}

export default memo(BatteryWidget)
